import { Box } from "@mui/material";
import { ReactComponent as CrossNew } from "assets/CrossNew.svg";
import { ReactComponent as DownloadIcon } from "assets/Download.svg";
import { ReactComponent as FileIcon } from "assets/FileIcon.svg";
import { ReactComponent as UploadUpIcon } from "assets/UploadUpIcon.svg";
import { ReactComponent as UploadIcon } from "assets/fileFolder.svg";
import ThemeContext from "components/app/Context";
import Button from "components/common/Button";
import CheckBox from "components/common/Checkbox";
import { setFeedbackDetails } from "components/common/Snackbar/snackbar.slice";
import Typography from "components/common/Typography";
import Validation from "components/common/Validation";
import Calender from "components/common/calender/Calender";
import { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { onDownloadClick } from "utils/CommonFunctions";
import { recommendationAccessTypes, responseTypes, serviceTypes } from "utils/Types";
import { uploadMatricsAsync, uploadRecommendationsAsync } from "./ManageSuggestions.slice";
import "./index.scss";

export default function UploadSuggestions() {
  const dispatch = useAppDispatch();
  const { isMobileScreen } = useContext(ThemeContext);
  const {
    setError,
    clearErrors,
    formState: { errors }
  } = useForm();

  const { uploadRecommendationAccess, uploadMetricAccess } = useAppSelector((state) => state.app.loggedInUserDetails);

  const [additionalAction, setAdditionalAction] = useState({
    matrixes: false,
    recommendation: false
  });
  const [selectedFile, setSelectedFile] = useState<any>();
  const [ischecked, setIsChecked] = useState<boolean>(false);
  const [asOfDate, setAsOfDate] = useState<any>();

  const hasUploadAccess =
    uploadRecommendationAccess === recommendationAccessTypes.upload ||
    uploadMetricAccess === recommendationAccessTypes.upload;

  const onFileChange = (event: any, type: any) => {
    clearErrors();

    if (event.target.files[0].type !== "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
      setError("selectedfile", {
        type: "filetype",
        message: "Whoa! Invalid file type."
      });
      return;
    }
    setSelectedFile(event.target.files[0]);
    setAdditionalAction(type);
  };

  const onFileUpload = async () => {
    let response;
    const formData = new FormData();
    formData.append("file", selectedFile);
    if (additionalAction.recommendation) {
      formData.append("uploadPreference", ischecked ? "OVERRIDE" : "RETAIN");
      response = await dispatch(uploadRecommendationsAsync(formData));
    } else {
      formData.append("asOfDate", asOfDate);
      response = await dispatch(uploadMatricsAsync(formData));
    }
    if (response?.payload?.status === responseTypes.created) {
      dispatch(
        setFeedbackDetails({
          shouldShow: true,
          variant: "success",
          setState: setFeedbackDetails({ shouldShow: false }),
          message: additionalAction.recommendation
            ? "Your file has been uploaded. It may take a few minutes to update the suggested plans."
            : "Your file has been uploaded. It may take a few minutes to import the metrics data"
        })
      );
      setSelectedFile(null);
    }
  };

  return (
    <div className="manage-suggestions-right-wrapper">
      <div className="upload-file-wrapper">
        <div className="upload-file-label">
          <Typography variant={"body2"} showBold color={tokens.Text.General.PrimaryHeadline}>
            Metrics / Recommendations
          </Typography>
          {hasUploadAccess && (
            <Typography variant={"body2"} color={tokens.Text.General.PrimaryHeadline}>
              Choose Upload type and select the excel file.
            </Typography>
          )}
        </div>

        {!selectedFile && hasUploadAccess && (
          <div className="upload-file-buttons" data-testid="upload_document_div">
            {uploadMetricAccess === recommendationAccessTypes.upload && (
              <div>
                <Box
                  component="input"
                  type="file"
                  accept=".xlsx"
                  className="choosefile-button"
                  onChange={(event) => onFileChange(event, { matrixes: true, recommendation: false })}
                />

                <Button
                  sx={{ cursor: "pointer", width: isMobileScreen ? "auto" : "232px" }}
                  variant="secondary"
                  startIcon={<UploadIcon />}
                  label={"Metrics"}
                />
              </div>
            )}
            {uploadRecommendationAccess === recommendationAccessTypes.upload && (
              <div>
                <Box
                  component="input"
                  type="file"
                  accept=".xlsx"
                  className="choosefile-button"
                  onChange={(event) => onFileChange(event, { matrixes: false, recommendation: true })}
                />
                <Button
                  sx={{ cursor: "pointer", width: isMobileScreen ? "auto" : "232px" }}
                  variant="secondary"
                  startIcon={<UploadIcon />}
                  label={"Recommendations"}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {(uploadMetricAccess === recommendationAccessTypes.download ||
        uploadMetricAccess === recommendationAccessTypes.upload) && (
        <div className="metrics-download-buttons">
          <Typography variant={"body2"} color={tokens.Text.General.PrimaryHeadline}>
            Download the metrics sheet
          </Typography>
          <Button
            sx={{ textDecoration: "underline", marginY: "0px", marginLeft: "-8px" }}
            label={"Download Metrics"}
            variant="text"
            startIcon={<DownloadIcon />}
            onClick={() => onDownloadClick("metrics/download", serviceTypes.recommendation)}
            size="Small"
          />
        </div>
      )}
      {errors.selectedfile && (
        <div className="upload-file-validation">
          <Validation name="file" type={errors.selectedfile?.type} message={"" + errors.selectedfile?.message} />
        </div>
      )}
      {selectedFile ? (
        <div className="uploaded-file">
          <div className="uploaded-file-name-container">
            <div className="uploaded-file-name-wrapper">
              <div className="create-left-wrapper">
                <div className="file-icon">
                  <FileIcon />
                </div>
                <div className="file-name-size">
                  <Typography variant="body2" showBold color={tokens.Text.General.PrimaryHeadline}>
                    {selectedFile ? selectedFile?.name : ""}
                  </Typography>
                  <Typography variant="body2" color={tokens.Text.General.Secondary}>
                    {selectedFile ? `${(selectedFile?.size / 1024 ** 2).toFixed(2)} MB` : ""}
                  </Typography>
                </div>
              </div>
              <div className="right-wrapper" onClick={() => setSelectedFile(null)}>
                <CrossNew />
              </div>
            </div>
          </div>
          {additionalAction.recommendation && (
            <div className="uploaded-file-checkbox">
              <CheckBox
                checked={ischecked}
                onChange={(event: any) => {
                  setIsChecked(event?.target?.checked);
                }}
              ></CheckBox>
              <Typography
                variant={"body2"}
                color={tokens.Text.General.PrimaryHeadline}
                children={"Override previous recommendations"}
              ></Typography>
            </div>
          )}
          {additionalAction.matrixes && (
            <div className="uploaded-file-date">
              <Calender
                fieldTitle="As Of Date"
                textFieldSx={{ borderRadius: "8px", backgroundColor: tokens.Background.General.Primary }}
                onChange={(event) => {
                  setAsOfDate(event);
                }}
                name={"name"}
                error={errors?.dueDate}
              />
            </div>
          )}
          <div className="uploaded-file-submit">
            <Button
              disabled={additionalAction.matrixes && !asOfDate}
              variant="primary"
              startIcon={<UploadUpIcon />}
              label={additionalAction.recommendation ? "Upload Recommendations" : "Upload Metrics"}
              onClick={onFileUpload}
            />
          </div>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
}
